import { useEffect, useMemo, useState } from "react";
import { mapSuggest } from "@/lib/api";
import { Network, Plus, Loader2, Sparkles } from "lucide-react";

const CONTENT_TYPES = ["katha", "mantra", "stotra", "sukta", "puja_vidhi", "festival"];
// Which form field each suggestion type applies to.
const TARGET = { deity: "deities", temple: "temples", category: "categories", tag: "tags" };
const GROUP_LABEL = {
  deity: "Deities", temple: "Temples", category: "Categories", tag: "Tags",
  related: "Related content",
};

// Auto-suggest related entities from the central Mappings based on what's
// already selected, and apply them to the form with one click.
export default function MappingSuggestions({ type, slug, deities = [], temples = [], categories = [], related = [], tags = [], onAdd }) {
  const [sug, setSug] = useState({});
  const [loading, setLoading] = useState(false);

  const seeds = useMemo(() => {
    const s = [];
    deities.forEach((x) => s.push(`deity:${x}`));
    temples.forEach((x) => s.push(`temple:${x}`));
    categories.forEach((x) => s.push(`category:${x}`));
    if (slug && type) s.push(`${type}:${slug}`);
    return s;
  }, [deities, temples, categories, type, slug]);

  useEffect(() => {
    if (seeds.length === 0) { setSug({}); return; }
    setLoading(true);
    mapSuggest(seeds).then((d) => setSug(d.suggestions || {})).catch(() => setSug({})).finally(() => setLoading(false));
  }, [seeds.join("|")]);

  // Build display groups: deity/temple/category/tag + a merged "related" (content types).
  const groups = useMemo(() => {
    const have = { deities: new Set(deities), temples: new Set(temples), categories: new Set(categories), tags: new Set(tags), related: new Set(related) };
    const g = {};
    Object.entries(sug).forEach(([t, arr]) => {
      const target = TARGET[t] || (CONTENT_TYPES.includes(t) ? "related" : null);
      if (!target) return;
      const key = TARGET[t] ? t : "related";
      const items = arr.filter((x) => !have[target].has(x.slug));
      if (items.length) g[key] = [...(g[key] || []), ...items.map((x) => ({ ...x, _target: target }))];
    });
    return g;
  }, [sug, deities, temples, categories, tags, related]);

  const keys = Object.keys(groups);
  if (seeds.length === 0) return null;

  const addOne = (item) => onAdd(item._target, [item.slug]);
  const addGroup = (items) => {
    const byField = {};
    items.forEach((x) => { byField[x._target] = [...(byField[x._target] || []), x.slug]; });
    Object.entries(byField).forEach(([f, slugs]) => onAdd(f, slugs));
  };

  return (
    <div className="border border-[var(--gold-muted)]/60 bg-[rgba(176,141,87,0.05)] p-4" data-testid="mapping-suggestions">
      <div className="mb-2 flex items-center gap-2">
        <Network className="h-4 w-4 text-gold" />
        <h4 className="text-sm font-medium text-ink">Suggested from Mappings</h4>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-saffron" />}
      </div>
      {keys.length === 0 ? (
        <p className="text-xs text-ink-3">{loading ? "Finding related entries…" : "No new suggestions — add a deity or temple above to see related mappings."}</p>
      ) : (
        <div className="space-y-3">
          {keys.map((k) => (
            <div key={k}>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="overline-label text-ink-3">{GROUP_LABEL[k] || k}</span>
                <button type="button" onClick={() => addGroup(groups[k])} data-testid={`map-sug-addall-${k}`} className="inline-flex items-center gap-1 text-xs text-saffron hover:underline">
                  <Sparkles className="h-3 w-3" /> Add all
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {groups[k].map((x) => (
                  <button key={`${x.type}:${x.slug}`} type="button" onClick={() => addOne(x)} data-testid={`map-sug-${x.slug}`}
                    className="inline-flex items-center gap-1 border border-line bg-surface px-2 py-1 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
                    <Plus className="h-3 w-3" /> {x.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
